import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Target, Shield, Users, Zap } from 'lucide-react';

const WhatIsKompaSection = () => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const features = [
    {
      icon: Target,
      title: 'Con propósito',
      desc: 'Cada aporte tiene una meta clara. No ahorras por ahorrar.',
      gradient: 'from-[#fbb040] to-yellow-500',
      color: '#fbb040'
    },
    {
      icon: Shield,
      title: 'Con respaldo',
      desc: 'Tu dinero protegido con alianzas bancarias y auditorías.',
      gradient: 'from-yellow-500 to-orange-500',
      color: '#fbbf40'
    },
    {
      icon: Users,
      title: 'Con acompañamiento',
      desc: 'Los KOMPAS te guían. KOMPAI disponible 24/7.',
      gradient: 'from-[#fbb040] to-yellow-600',
      color: '#fbb040'
    },
  ];

  return (
    <section id="que-es-kompa" ref={ref} className="relative py-32 bg-gradient-to-br from-white via-yellow-50 to-white overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="inline-block mb-6"
          >
            <Zap size={48} className="text-[#fbb040]" strokeWidth={3} />
          </motion.div>
          
          <h2 className="text-5xl md:text-7xl font-black mb-6 flex items-center justify-center gap-3 flex-wrap">
            <span>¿Qué es</span>
            <span className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_cb23de51-57d5-4133-8f16-28731c09c0ec/artifacts/y3iiaaqn_KOMPA%207.png"
                alt="KOMPA"
                className="h-16 md:h-28 inline-block"
                style={{ 
                  filter: 'drop-shadow(0 4px 12px rgba(251, 176, 64, 0.4))',
                  marginTop: '8px'
                }}
              />
              <span>?</span>
            </span>
          </h2>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4 }}
            className="text-2xl text-gray-700 max-w-3xl mx-auto font-bold"
          >
            Una plataforma de finanzas personales{' '}
            <span className="text-[#fbb040] font-black">con propósito</span>,<br />
            creada para&nbsp;ti.
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 100, rotateY: -90 }}
              animate={inView ? { opacity: 1, y: 0, rotateY: 0 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.2,
                type: "spring"
              }}
              whileHover={{ 
                scale: 1.05, 
                y: -20,
                rotateY: 5,
              }}
              className="group relative"
              style={{ perspective: 1000 }}
            >
              {/* Glow effect */}
              <motion.div 
                className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-3xl blur-2xl opacity-30 group-hover:opacity-60 transition-opacity`}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              {/* Card */}
              <div className="relative bg-white p-10 rounded-3xl border-4 border-[#fbb040] hover:border-yellow-400 transition-all shadow-2xl overflow-hidden min-h-[380px] flex flex-col">
                {/* Icon with animated border */}
                <motion.div
                  className="relative mb-8"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    className={`absolute inset-0 w-28 h-28 bg-gradient-to-r ${feature.gradient} rounded-2xl blur-md`}
                  />
                  <div className={`relative w-28 h-28 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center shadow-2xl border-4 border-white`}>
                    <feature.icon size={56} className="text-black" strokeWidth={3} />
                  </div>
                </motion.div>
                
                {/* Content */}
                <h3 className="text-3xl font-black text-black mb-4 relative z-10">{feature.title}</h3>
                <p className="text-gray-700 text-xl font-semibold relative z-10 flex-grow">{feature.desc}</p>
                
                {/* Sparkle effect */}
                <motion.div
                  animate={{ 
                    scale: [0, 1, 0],
                    rotate: [0, 180, 360],
                  }}
                  transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                  className="absolute top-4 right-4 w-8 h-8"
                >
                  <Zap size={32} className="text-[#fbb040]" fill="currentColor" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatIsKompaSection;